// `/for-teachers/odyssey` — the classroom pitch, set as a departmental
// circular bound into the same edition as the shelf and the author's note.
//
// THE ARGUMENT IS THE LAYOUT. A teacher's first question about a retelling is
// whether it is an abridgment, and no adjective answers that as fast as the
// specimen does: this translation in the left column, Homer's Greek in the
// right (the reader's own arrangement), one hairline gutter between them, both
// from Book 9 — the book their syllabus actually assigns, and one they can
// open free on the way out of this page. Everything above the specimen exists
// to get them to it.
//
// The offer (plan §3) is set as a printed notice rather than a call-to-action
// panel, because it reads as a promise from a publisher and not as a banner.
// The actions under it are gated; `ClassroomActions` owns that and says why.
//
// PUBLIC. Nothing here requires a session — a signed-out visitor sees the whole
// pitch and the whole offer, and meets the gate only at the actions.

import { Link } from "react-router";
import { BookOpen } from "lucide-react";
import { ErrorBoundary } from "@bindery/ui/components/error-boundary";
import { ClassroomActions } from "./classroom-actions";
import { ReviewAccessRequest } from "./review-access-request";

/** A small-caps section mark — the same device the about page opens with. */
function SectionMark({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-muted-foreground pt-10 text-center font-serif text-[0.75rem] tracking-[0.35em] uppercase">
      {children}
    </h2>
  );
}

/**
 * A context link as the reader sets it, standing still. The reader's trigger
 * (`reader/context-link.tsx`) is a focusable button role that opens the
 * lookup drawer; here the phrase only has to look the part, so it keeps the
 * underline and nothing a keyboard would try to press.
 */
function SpecimenContextLink({ children }: { children: React.ReactNode }) {
  return (
    <span className="decoration-foreground/40 rounded-[2px] underline decoration-dotted decoration-[0.08em] underline-offset-[0.22em]">
      {children}
    </span>
  );
}

/**
 * The specimen: Book 9, lines 19–21, in both columns.
 *
 * The Greek is Homer's (public domain, and the same text the reader serves);
 * the English is this translation's own. English on the left, Greek on the
 * right — the reader's own arrangement. Stacked on a narrow screen with the
 * gutter turning into a rule, because two 20-character columns are not a
 * facing-page spread, they are a mess.
 *
 * `lang` on both halves is not decoration: it is what lets a screen reader
 * pronounce the Greek as Greek and stops a browser hyphenating it as English.
 */
function Specimen() {
  return (
    <figure className="mt-10">
      <div className="grid gap-6 border-y border-[--rule] py-8 sm:grid-cols-2 sm:gap-0">
        <blockquote
          lang="en"
          className="text-foreground font-serif text-[1.05rem] leading-relaxed sm:border-r sm:border-[--rule] sm:pr-8"
        >
          <p>
            I am Odysseus, son of Laertes. The whole world knows my clever
            plans, and my fame reaches the sky. My home is{" "}
            <SpecimenContextLink>Ithaca</SpecimenContextLink>, the bright
            island.
          </p>
        </blockquote>
        <blockquote
          lang="grc"
          className="text-muted-foreground border-t border-[--rule] pt-6 font-serif text-[1.02rem] leading-[1.9] sm:border-t-0 sm:pt-0 sm:pl-8"
        >
          <p className="whitespace-pre-line">
            {"εἴμ’ Ὀδυσεὺς Λαερτιάδης, ὃς πᾶσι δόλοισιν\n" +
              "ἀνθρώποισι μέλω, καί μευ κλέος οὐρανὸν ἵκει.\n" +
              "ναιετάω δ’ Ἰθάκην εὐδείελον·"}
          </p>
        </blockquote>
      </div>
      <figcaption className="text-muted-foreground mt-3 text-center font-serif text-sm italic">
        Book 9, lines 19–21 — this translation beside Homer&rsquo;s Greek.
      </figcaption>
    </figure>
  );
}

/** What the dotted underline in the specimen is, said once, in the body face. */
function BackgroundNotes() {
  return (
    <p className="text-foreground mt-8 font-serif text-[1.05rem] leading-relaxed">
      The dotted line under <em>Ithaca</em> runs through the whole retelling.
      Names, places and customs a first-time reader meets without introduction
      carry it, and a tap opens a short background note from Simple English
      Wikipedia, with a longer passage to read on into. Each note names the
      article and the revision it was taken from, and is Wikipedia&rsquo;s under
      its Creative Commons licence, not ours — there so a student is never
      stopped by a name.
    </p>
  );
}

/**
 * The measured reading level, set as a contents-page entry.
 *
 * DOTTED LEADERS AND ALEGREYA NUMERALS are the shelf's device for a fact about
 * an edition (`library-page.tsx` uses them for the table of contents), which is
 * exactly the register this belongs in: a catalogued fact, not a claim. Set as
 * a marketing statistic it would invite the suspicion the whole page exists to
 * answer.
 *
 * ATOS AND ONLY ATOS, and always with the measure named — `docs/ops/reading-
 * level.md` §6 has the reasoning. No Lexile (MetaMetrics' trademark, and not
 * what we hold), and no bare grade: the same book is 2.1 by Flesch-Kincaid and
 * 4.2 by ATOS, so a number with no instrument attached says nothing a teacher
 * can check. The outbound link to Renaissance's own explainer is what carries
 * the checkability claim — a named instrument the reader can go read about.
 */
function ReadingLevel() {
  const volumes: readonly { label: string; books: string; atos: string }[] = [
    { label: "Volume one", books: "Books 1–4", atos: "4.2" },
    { label: "Volume two", books: "Books 5–12", atos: "4.3" },
    { label: "Volume three", books: "Books 13–24", atos: "5.0" },
  ];
  return (
    <div className="mt-8">
      <dl className="space-y-3">
        {volumes.map((v) => (
          <div key={v.label} className="flex items-baseline gap-3">
            <dt className="text-foreground font-serif text-[1.05rem]">
              {v.label}
              <span className="text-muted-foreground"> · {v.books}</span>
            </dt>
            {/* The leader. Decorative, so it is hidden from the reading order —
                a screen reader gets "Volume one, Books 1-4: 4.2". */}
            <span
              aria-hidden="true"
              className="mb-[0.35em] flex-1 border-b border-dotted border-[--rule]"
            />
            <dd className="text-foreground font-serif text-[1.15rem]">
              {v.atos}
            </dd>
          </div>
        ))}
      </dl>
      <p className="text-muted-foreground mt-5 font-serif text-[1.02rem] leading-relaxed">
        That is the{" "}
        <a
          href="https://www.renaissance.com/edword/atos/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-foreground hover:text-primary underline decoration-dotted underline-offset-4"
        >
          ATOS book level
        </a>{" "}
        — the Accelerated Reader scale on your library&rsquo;s spine stickers.
        It is a grade scale, so this is a fourth- to fifth-grade reading level,
        rising as the story goes on — worth knowing when you plan a sequence.
      </p>
    </div>
  );
}

/** What a class can open without paying anything, stated as a fact rather than
 *  a teaser — a teacher plans against the list, so the list has to be exact. */
function WhatIsFree() {
  return (
    <div className="mt-8 border border-[--rule] px-6 py-5">
      <p className="text-muted-foreground font-serif text-sm tracking-[0.2em] uppercase">
        Free to anyone, no account
      </p>
      <p className="text-foreground mt-3 font-serif text-[1.05rem] leading-relaxed">
        <span className="font-serif">Books 1–5</span>, and{" "}
        <span className="font-serif">9</span>,{" "}
        <span className="font-serif">13</span> and{" "}
        <span className="font-serif">21</span> — the whole of volume one, plus
        the cyclops and the contest of the bow.
      </p>
      <p className="text-muted-foreground mt-2 font-serif text-[0.95rem] leading-relaxed">
        The rest of the twenty-four is $8 a volume.
      </p>
      {/* A new tab, like every link off this page: the teacher is mid-pitch,
          and the page is the thing they will want to come back to and cite
          after the chapter. React Router leaves targeted clicks to the
          browser, so this is a plain full-page navigation. */}
      <Link
        to="/read/odyssey/9"
        target="_blank"
        rel="noopener"
        className="border-foreground text-foreground hover:border-primary hover:text-primary mt-4 inline-flex items-center gap-2 border-b pb-1 font-serif text-sm tracking-[0.2em] uppercase transition-colors"
      >
        <BookOpen aria-hidden="true" className="size-4" />
        Open Book 9
      </Link>
    </div>
  );
}

/** The competitive fact (plan §5), set as a bibliographic note rather than a
 *  comparison table — it is an argument about editions, and editions are what
 *  a bibliography is for. */
function TranslationNote() {
  return (
    <div className="text-muted-foreground mt-8 space-y-4 font-serif text-[1.02rem] leading-relaxed">
      <p>
        Every Odyssey a US high school actually assigns is in copyright —
        Fitzgerald, which is the one printed in the Holt and McDougal
        ninth-grade literature books, along with Fagles, Lattimore, Lombardo and
        Wilson. Every translation that is free is Victorian or older: Butler,
        Butcher and Lang, Cowper, Pope, Chapman.
      </p>
      <p className="text-foreground">
        So the choice a department is usually offered is to pay for each student
        to hold a modern translation, or to assign prose written in 1879. This
        is a third option.
      </p>
    </div>
  );
}

/**
 * The offer (plan §3), as a printed notice.
 *
 * Deliberately not a card, not a gradient, not a button in a coloured box: a
 * ruled block in the body face, because the sentence is unusual enough that
 * dressing it up would make it read as marketing rather than as a commitment.
 */
function Notice() {
  return (
    <aside className="border-foreground relative mt-10 border-2 px-7 py-8">
      <p className="bg-background text-muted-foreground absolute -top-[0.6rem] left-6 max-w-[calc(100%-3rem)] px-3 font-serif text-[0.6rem] tracking-[0.2em] whitespace-nowrap uppercase sm:text-[0.7rem] sm:tracking-[0.35em]">
        A notice from the publisher
      </p>
      <p className="text-foreground font-serif text-2xl leading-snug font-semibold tracking-tight text-balance">
        Your class can read this free. You only have to ask.
      </p>
      <div className="text-foreground mt-4 space-y-3 font-serif text-[1.05rem] leading-relaxed">
        <p>
          Send your school or district’s purchasing decision-maker a link, or
          send us their contact and we will make the approach ourselves. We will
          find a price your district is comfortable with, and where a district
          cannot afford it we will find or provide grant funding.
        </p>
        <p className="font-medium">
          Funding is not going to be the reason your class doesn’t get this.
        </p>
      </div>
    </aside>
  );
}

/** What stands in for the actions when their queries cannot load. It still has
 *  to carry the offer somewhere the teacher can act on it, so it falls back to
 *  the thing that needs no backend at all: writing to a person. */
function ActionsUnavailable() {
  return (
    <section className="mt-10 border border-[--rule] px-6 py-6">
      <h2 className="text-muted-foreground font-serif text-[0.75rem] tracking-[0.35em] uppercase">
        Ask for your class
      </h2>
      <p className="text-foreground mt-4 font-serif text-[1.05rem] leading-relaxed">
        This part of the page isn’t loading just now. The offer above still
        stands — write to{" "}
        <a
          href="mailto:rf@sara.ai?subject=The%20Odyssey%20for%20my%20class"
          className="underline decoration-dotted underline-offset-4"
        >
          rf@sara.ai
        </a>{" "}
        and we will take it from there.
      </p>
    </section>
  );
}

export function TeacherPage() {
  return (
    <div className="bg-background min-h-dvh">
      <main className="mx-auto max-w-2xl px-6 pt-20 pb-16 sm:px-8">
        {/* Masthead — matches the library's title page and the author's note. */}
        <header className="text-center">
          <p className="text-muted-foreground font-serif text-sm tracking-[0.35em] uppercase">
            For teachers
          </p>
          <h1 className="text-foreground mt-3 font-serif text-5xl font-semibold tracking-tight text-balance sm:text-6xl">
            The Odyssey, in a translation your students can read
          </h1>
          <div
            aria-hidden="true"
            className="border-border mx-auto mt-10 w-24 border-t"
          />
        </header>

        <article className="text-foreground space-y-6 pt-10 font-serif text-[1.05rem] leading-relaxed">
          <p className="first-letter:float-left first-letter:mt-1 first-letter:pr-3 first-letter:font-serif first-letter:text-[3.4rem] first-letter:leading-[0.8] first-letter:font-semibold">
            This is a translation, not an abridgment and not a summary. All
            twenty-four books, out of Homer’s Greek, with every load-bearing
            beat carried — the tale told to the Phaeacians, the underworld, the
            bow, the olive-tree bed. What is different is the register: plain
            modern English, easy enough to read aloud to a second-grader, for a
            fourth-grader to read alone, and for an adult meeting the Odyssey
            for the first time to finish in a few evenings.
          </p>
          <p>
            It does not replace the verse translations — Wilson’s and Fagles’s
            poetry, or Homer’s own Greek — and does not try to. It is a gateway:
            the full narrative, at a readability that lets students finish it.
            In classrooms from fourth grade up it can simply be the Odyssey; in
            a ninth-grade room it can carry the assigned reading while chosen
            passages are set beside Wilson or Fagles for the fuller poetry,
            because a student who already knows the story can afford to study
            the verse.
          </p>
        </article>

        <SectionMark>The same page, twice</SectionMark>
        <Specimen />
        <BackgroundNotes />

        <SectionMark>What level it reads at</SectionMark>
        <ReadingLevel />

        <SectionMark>What it costs your students</SectionMark>
        <WhatIsFree />

        <SectionMark>Why the free ones are Victorian</SectionMark>
        <TranslationNote />

        <Notice />

        {/* The gated half. Everything above is the pitch and is public; these
            are promises, and they ask for something first.

            BOUNDED, because the pitch must outlive the backend. Both blocks
            below read a Convex query, and an unreachable — or not-yet-deployed
            — backend throws where the client cannot catch it, which would take
            the whole circular down with it and leave a teacher a blank page.
            The argument above is static, so it should still be readable when
            the machinery under the actions is not. */}
        <ErrorBoundary fallback={() => <ActionsUnavailable />}>
          <ClassroomActions />
          {/* The affordability path, which must never sit behind the price of
              the copy — that would make the gate the roadblock the notice above
              promises to remove (plan §3b). */}
          <ReviewAccessRequest />
        </ErrorBoundary>

        <p className="text-muted-foreground mt-14 text-right font-serif text-[1.05rem] italic">
          — Russ T. Fugal
        </p>

        <div className="mt-8 flex justify-center">
          <Link
            to="/"
            target="_blank"
            rel="noopener"
            className="border-foreground text-foreground hover:border-primary hover:text-primary border-b pb-1 font-serif text-sm tracking-[0.2em] uppercase transition-colors"
          >
            Browse the shelf
          </Link>
        </div>
      </main>
    </div>
  );
}
